import { useReducer, useRef, useState } from 'react';
import listingsReducer from '@Components/Listings/ListingsReducer';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { eventCategory } from '@Components/Utilities/SandhillsConstants';
import translator from '@Components/Utilities/Translator';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useRecoilValue, useRecoilState } from 'recoil';

const AlertPreferenceModal = loadable(() => import('@Components/Shared/Modals/AlertPreferenceModal'), true);
const BiddingAgreementModal = loadable(() => import('@Components/Shared/Modals/BiddingAgreementModal'), true);

const EventListing = loadable(() => import('@Components/Events/EventListing'));
const MemoEventListing = React.memo(EventListing);

const EventSection = (props) => {
    const EVENTS_UPDATE_INTERVAL = 60000;
    const MAX_DESCRIPTION_LENGTH = 100;

    const eventsTimer = useRef(null);
    const routes = useRecoilValue(atoms.routes);
    
    const [eventIDForAlertPreferenceModal, setEventIDForAlertPreferenceModal] = useState(0);
    const [eventCategoryIDForAlertPreferenceModal, setEventCategoryIDForAlertPreferenceModal] = useState(0);
    const [showAlertPreferenceModal, setShowAlertPreferenceModal] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState(null);
    
    const [biddingAgreementProps, setBiddingAgreementProps] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showBiddingAgreementModal, setShowBiddingAgreementModal] = useState(false);

    const [listEvents, dispatchListEvents] = useReducer(listingsReducer, props.Events);
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);

    /* ALERT PREFERENCES */
    const openAlertPreferenceModal = (eventID, eventCategoryID) => {
        if (!currentUser.IsAuthenticated) {
            window.location.href = routes.SSO;
        }

        if (eventID && eventID > 0) {
            setEventIDForAlertPreferenceModal(eventID);
        }

        let siteName = "";

        if (eventCategoryID && eventCategoryID > 0) {
            setEventCategoryIDForAlertPreferenceModal(eventCategoryID);
            siteName = eventCategoryID === eventCategory.onlineAuction ? "AuctionTime" : "EquipmentFacts";
        } 

        if (!isLoading) {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.GetUserAuctionAlertPreferences,
                method: 'post',
                data: {
                    siteName: siteName 
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                setIsLoading(false);
                if (data) {
                    setNotificationSettings(data);
                    setShowAlertPreferenceModal(!showAlertPreferenceModal);
                }
            }).catch(() => {
                setIsLoading(false);
            });
        }
    };

    /* T&C */
    const toggleBiddingAgreementModal = (data) => {
        !showBiddingAgreementModal ? setBiddingAgreementProps(data) : setBiddingAgreementProps(null);
        setShowBiddingAgreementModal(!showBiddingAgreementModal);
    };


    const renderEvent = (event, index) => {
        return (
            <MemoEventListing
                {...event}
                IsWatchList={false}
                setListings={dispatchListEvents}
                listings={listEvents}
                openBiddingAgreementModal={toggleBiddingAgreementModal}
                openAlertPreferenceModal={openAlertPreferenceModal}
                ShowAuctionBranding={true}
                IsRecommendedAuctionsSearch={false}
                ShowAltBackground={index % 2 == 0}
                MaxDescriptionLength={MAX_DESCRIPTION_LENGTH}
                HideLiveBorder={true}
            />
        );
    }

    return (
        <div className='events-section'>
            {props.IsLiveEventSection ?
                <div className='section-title'>
                    <h1 className='section-title-text'>
                        {props.SectionTitle}
                    </h1>
                </div>
                :
                <div className='section-title'>
                    {props.NoLiveEvents ?
                        <h1 className='section-title-text'>
                            {props.SectionTitle}
                        </h1>
                        :
                        <h2 className='section-title-text'>
                            {props.SectionTitle}
                        </h2>
                    }
                </div>
            }
            <div className={'live-event-list' + (props.IsLiveEventSection ? ' live-active' : '')}>
                {listEvents && listEvents.length > 0 &&
                    listEvents.map((event, index) => renderEvent(event, index))}
            </div>

            {!props.IsLiveEventSection &&
                <div className='upcoming-pagination'>
                    <span dangerouslySetInnerHTML={{ __html: props.UpcomingPaginationTitle }} />
                    <br />
                    <a className='ts-button view-all-btn' href={props.UpcomingEventsUrl}>
                        {translator.translate('sandhills.tradepubwebsite.mvc.viewall')}
                    </a>
                </div>
            }

            {(showAlertPreferenceModal && eventIDForAlertPreferenceModal) &&
                <AlertPreferenceModal
                    {...notificationSettings}
                    onClose={(e) => { setShowAlertPreferenceModal(false); }}
                    EventID={eventIDForAlertPreferenceModal}
                    EventCategoryID={eventCategoryIDForAlertPreferenceModal}
                />}

            {(showBiddingAgreementModal && biddingAgreementProps) &&
                <BiddingAgreementModal
                    {...biddingAgreementProps}
                    onClose={toggleBiddingAgreementModal}
                />}

            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>
    );
}

export default EventSection;